class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    document.dispatchEvent(new CustomEvent('filters:updated'));
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    // Snapshot which mobile filters are currently open before re-render
    const openFilterIds = new Set();
    document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details[open]').forEach((el) => {
      openFilterIds.add(el.id);
    });

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
        const isTextInput = event.target.getAttribute('type') === 'text';
        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    // Restore open state for filters that were open before re-render
    openFilterIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.setAttribute('open', '');
    });

    // Also ensure any filter with active selections stays open
    document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details').forEach((details) => {
      if (details.querySelector('input[type="checkbox"]:checked')) {
        details.setAttribute('open', '');
      }
    });

    // Cosmetic enhancements — no expansion logic here
    FacetFiltersForm.hideZeroCountOptions();
    FacetFiltersForm.updateSelectedCounts();
    FacetFiltersForm.injectColourSwatches();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];
    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [{ section: document.getElementById('product-grid').dataset.id }];
  }

  // ─── FUNCTIONAL ENHANCEMENTS ─────────────────────────────────────────────────

  static hideZeroCountOptions() {
    document.querySelectorAll('#FacetFiltersForm .facets__item, #FacetSortDrawerForm .facets__item').forEach((li) => {
      const input = li.querySelector('input[type="checkbox"]');
      if (!input) return;
      const countText = li.querySelector('.facet-checkbox__text-label')?.nextSibling?.textContent?.trim() || '';
      const count = parseInt(countText.replace(/[()]/g, ''), 10);
      li.style.display = (!isNaN(count) && count === 0 && !input.checked) ? 'none' : '';
    });

    document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__item').forEach((li) => {
      const input = li.querySelector('input[type="checkbox"]');
      if (!input) return;
      const countText = li.querySelector('.facet-checkbox__text-label')?.nextSibling?.textContent?.trim() || '';
      const count = parseInt(countText.replace(/[()]/g, ''), 10);
      li.style.display = (!isNaN(count) && count === 0 && !input.checked) ? 'none' : '';
    });
  }

  static updateSelectedCounts() {
    document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details').forEach((details) => {
      const summary = details.querySelector('.mobile-facets__summary');
      if (!summary) return;
      summary.querySelector('.om-filter-badge')?.remove();
      const checkedCount = details.querySelectorAll('input[type="checkbox"]:checked').length;
      if (checkedCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'om-filter-badge';
        badge.textContent = checkedCount;
        summary.querySelector('div')?.appendChild(badge);
      }
    });

    document.querySelectorAll('#FacetFiltersForm .js-filter, #FacetSortDrawerForm .js-filter').forEach((details) => {
      const summary = details.querySelector('.facets__summary');
      if (!summary) return;
      summary.querySelector('.om-filter-badge')?.remove();
      const checkedCount = details.querySelectorAll('input[type="checkbox"]:checked').length;
      if (checkedCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'om-filter-badge';
        badge.textContent = checkedCount;
        summary.querySelector('div')?.appendChild(badge);
      }
    });
  }

  /**
   * Close all filter groups on initial load.
   * Filters open server-side for no-JS fallback but start closed in JS context.
   */
  static closeAllFilters() {
    document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details').forEach((details) => {
      // Keep open if it has active selections
      const hasActive = details.querySelector('input[type="checkbox"]:checked');
      if (!hasActive) details.removeAttribute('open');
    });
  }

  /**
   * Inject a colour swatch dot next to each label in the Colour filter group.
   * Works by reading the label text and matching against a colour map.
   */
  static injectColourSwatches() {
    const colourMap = {
  'black': '#1a1814', 'white': '#ffffff', 'neutral': '#E8E2DA',
  'cream': '#FAF8F6', 'grey': '#9E9E9E', 'gray': '#9E9E9E',
  'pink': '#F4C2C2', 'red': '#b21c0bff', 'green': '#a3c7aeff',
  'blue': '#5191d5ff', 'dark blue': '#1B3A6B', 'light blue': '#AED6F1',
  'navy': '#1B2A6B', 'yellow': '#f3e4a7ff', 'brown': '#795548',
  'dark brown': '#4E342E', 'khaki': '#B5A98A', 'fig': '#6D3B5A',
  'choc': '#5D3A1A', 'natural': '#D4C5A9', 'purple': '#7B4F8E',
  'orange': '#E8834A', 'gold': '#C9A84C', 'silver': '#A8A8A8',
  'mixed': null, 'polka dot': null, 'multicolour': null,
  'in stock': '#4A7C59', 'out of stock': '#d4cfc9',
  'dresses': null, 'tops': null, 'skirts': null, 'trousers': null,
  'coats': null, 'jumpsuits': null, 'sets': null, 'accessories': null,
};

    document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details').forEach((details) => {
      const heading = details.querySelector('.mobile-facets__summary > span:first-child');
      if (!heading) return;
      const groupName = heading.textContent.trim().toLowerCase();
      // Only inject for colour-type filter groups
      if (!groupName.includes('colour') && !groupName.includes('color')) return;

      details.querySelectorAll('.mobile-facets__item').forEach((item) => {
        const label = item.querySelector('.mobile-facets__label');
        const textLabel = item.querySelector('.facet-checkbox__text-label');
        if (!label || !textLabel || label.querySelector('.om-swatch')) return;

        const colourName = textLabel.textContent.trim().toLowerCase();
        const colour = colourMap[colourName];
        if (colour === undefined) return; // unknown colour — skip

        const swatch = document.createElement('span');
        swatch.className = 'om-swatch';
        swatch.setAttribute('data-colour', colourName);
        if (colour) swatch.style.backgroundColor = colour;
        // Insert after checkbox, before text
        const checkbox = label.querySelector('.mobile-facets__checkbox');
        if (checkbox) {
          checkbox.after(swatch);
        } else {
          label.prepend(swatch);
        }
      });
    });
  }

  // ─── INSTANCE METHODS ────────────────────────────────────────────────────────

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// On load: cosmetic enhancements + close all filters + inject swatches
document.addEventListener('DOMContentLoaded', () => {
  FacetFiltersForm.hideZeroCountOptions();
  FacetFiltersForm.updateSelectedCounts();
  FacetFiltersForm.closeAllFilters();
  FacetFiltersForm.injectColourSwatches();
});

// ─── PRICE RANGE ──────────────────────────────────────────────────────────────

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

// ─── FACET REMOVE ─────────────────────────────────────────────────────────────

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
// ─── CUSTOM SORT ──────────────────────────────────────────────────────────────
document.addEventListener('click', (e) => {
  const header = e.target.closest('.om-sort-header');
  const option = e.target.closest('.om-sort-option');
  const row = e.target.closest('.om-sort-row');

  if (header) {
    row.classList.toggle('om-sort-open');
    return;
  }

  if (option) {
    const value = option.dataset.value;
    const input = document.getElementById('SortBy-mobile');
    const current = document.getElementById('om-sort-current');
    if (input) {
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));  // ← add this
    }
    if (current) current.textContent = option.textContent.trim();
    row.querySelectorAll('.om-sort-option').forEach(o => o.classList.remove('om-sort-option--active'));
    option.classList.add('om-sort-option--active');
    row.classList.remove('om-sort-open');
    return;
  }

  // Close if clicking outside
  document.querySelectorAll('.om-sort-row.om-sort-open').forEach(r => {
    if (!r.contains(e.target)) r.classList.remove('om-sort-open');
  });
});